import React, { useEffect, useState } from 'react';
import { parseEmail, testEmailParse, getEmailSourcedEnquiries, getCompanies } from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Mail, 
  FileText, 
  CheckCircle2, 
  AlertCircle, 
  Zap, 
  Eye, 
  Inbox,
  Send,
  Clock,
  User,
  Truck,
  Calendar,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

export const EmailParserPage = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [emailSourcedEnquiries, setEmailSourcedEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Manual email input
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [senderName, setSenderName] = useState('');
  
  // Test result
  const [testResult, setTestResult] = useState(null);
  const [testDialogOpen, setTestDialogOpen] = useState(false);

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      loadEmailSourcedEnquiries();
    }
  }, [selectedCompany]);

  const loadCompanies = async () => {
    try {
      const response = await getCompanies();
      setCompanies(response.data);
      if (response.data.length > 0) {
        setSelectedCompany(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to load companies:', error);
    }
  };

  const loadEmailSourcedEnquiries = async () => {
    try {
      const response = await getEmailSourcedEnquiries(selectedCompany);
      setEmailSourcedEnquiries(response.data);
    } catch (error) {
      console.error('Failed to load email-sourced enquiries:', error);
    }
  };

  const handleTestParse = async () => {
    if (!emailSubject && !emailBody) {
      toast.error('Please enter email subject or body');
      return;
    }
    if (!senderEmail) {
      toast.error('Please enter sender email');
      return;
    }

    setLoading(true);
    try {
      const response = await testEmailParse({
        subject: emailSubject,
        body: emailBody,
        sender_email: senderEmail,
        sender_name: senderName
      });
      setTestResult(response.data);
      setTestDialogOpen(true);
      toast.success('Email parsed successfully!');
    } catch (error) {
      toast.error('Failed to parse email');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEnquiry = async () => {
    if (!emailSubject && !emailBody) {
      toast.error('Please enter email subject or body');
      return;
    }
    if (!senderEmail) {
      toast.error('Please enter sender email');
      return;
    }
    if (!selectedCompany) {
      toast.error('Please select a company');
      return;
    }

    setLoading(true);
    try {
      const response = await parseEmail({
        subject: emailSubject,
        body: emailBody,
        sender_email: senderEmail,
        sender_name: senderName,
        company_id: selectedCompany
      });
      toast.success('Enquiry created from email!');
      
      // Clear form
      setEmailSubject('');
      setEmailBody('');
      setSenderEmail('');
      setSenderName('');
      
      // Reload enquiries
      loadEmailSourcedEnquiries();
    } catch (error) {
      toast.error('Failed to create enquiry');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnectGmail = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/email-parser';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Sample email templates for testing
  const sampleEmails = [
    {
      subject: "Excavator Rental Inquiry - 3 months project",
      body: "Dear Sir,\n\nWe are looking to rent 2 excavators with operators for our construction project in Riyadh. The project duration is approximately 3 months starting from next month.\n\nPlease send us your best quotation.\n\nBest regards,\nMohamed Al-Rashid\n+966 50 123 4567",
      sender: "mohamed@construction-co.sa"
    },
    {
      subject: "Need 5 pickup trucks for 6 months",
      body: "Hello,\n\nWe require 5 pickup trucks (4x4) for our oil field operations. Duration: 6 months minimum. We need them with drivers.\n\nKindly provide your rates.\n\nThanks,\nAhmed",
      sender: "ahmed@oilfield-services.com"
    },
    {
      subject: "Bus rental for employee transportation",
      body: "Hi,\n\nOur company needs 3 buses (50 seater) with chauffeurs for daily employee transportation. Contract period: 1 year.\n\nPlease call me to discuss: 0551234567",
      sender: "hr@bigcompany.sa"
    }
  ];

  const loadSampleEmail = (sample) => {
    setEmailSubject(sample.subject);
    setEmailBody(sample.body);
    setSenderEmail(sample.sender);
    setSenderName('');
    toast.info('Sample email loaded');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black tracking-tight" data-testid="email-parser-title">
            Email Parser
          </h1>
          <p className="text-muted-foreground mt-1">
            Automatically create enquiries from incoming emails
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedCompany} onValueChange={setSelectedCompany}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Select Company" />
            </SelectTrigger>
            <SelectContent>
              {companies.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button 
            onClick={handleConnectGmail}
            className="bg-red-500 hover:bg-red-600"
            data-testid="connect-gmail-btn"
          >
            <Mail className="h-4 w-4 mr-2" />
            Connect Gmail
          </Button>
        </div>
      </div>

      <Tabs defaultValue="manual" className="space-y-6">
        <TabsList>
          <TabsTrigger value="manual">Manual Input</TabsTrigger>
          <TabsTrigger value="history">Parsed Enquiries ({emailSourcedEnquiries.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="manual" className="space-y-6">
          {/* How it works */}
          <Card className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-white rounded-lg shadow-sm">
                <Sparkles className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-blue-900">How Email Parser Works</h3>
                <p className="text-blue-700 mt-1">
                  Paste an email below and our AI will automatically extract:
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                  <div className="flex items-center gap-2 text-sm text-blue-800">
                    <User className="h-4 w-4" /> Client Info
                  </div>
                  <div className="flex items-center gap-2 text-sm text-blue-800">
                    <Truck className="h-4 w-4" /> Asset Type
                  </div>
                  <div className="flex items-center gap-2 text-sm text-blue-800">
                    <Calendar className="h-4 w-4" /> Duration
                  </div>
                  <div className="flex items-center gap-2 text-sm text-blue-800">
                    <CheckCircle2 className="h-4 w-4" /> Requirements
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Sample Emails */}
          <Card className="p-4">
            <h4 className="font-semibold mb-3 flex items-center gap-2">
              <Inbox className="h-4 w-4" />
              Try Sample Emails
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {sampleEmails.map((sample, index) => (
                <Button
                  key={index}
                  variant="outline"
                  className="h-auto p-4 text-left flex flex-col items-start"
                  onClick={() => loadSampleEmail(sample)}
                >
                  <span className="font-semibold text-sm line-clamp-1">{sample.subject}</span>
                  <span className="text-xs text-muted-foreground mt-1">{sample.sender}</span>
                </Button>
              ))}
            </div>
          </Card>

          {/* Email Input Form */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="p-6">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Email Content
              </h3>
              <div className="space-y-4">
                <div>
                  <Label>Sender Email *</Label>
                  <Input
                    type="email"
                    value={senderEmail}
                    onChange={(e) => setSenderEmail(e.target.value)}
                    placeholder="client@company.com"
                    data-testid="sender-email-input"
                  />
                </div>
                <div>
                  <Label>Sender Name (Optional)</Label>
                  <Input
                    value={senderName}
                    onChange={(e) => setSenderName(e.target.value)}
                    placeholder="John Doe"
                    data-testid="sender-name-input"
                  />
                </div>
                <div>
                  <Label>Email Subject *</Label>
                  <Input
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    placeholder="Excavator rental inquiry..."
                    data-testid="email-subject-input"
                  />
                </div>
                <div>
                  <Label>Email Body *</Label>
                  <Textarea
                    value={emailBody}
                    onChange={(e) => setEmailBody(e.target.value)}
                    placeholder="Dear Sir, we are looking to rent..."
                    rows={8}
                    data-testid="email-body-input"
                  />
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                <Zap className="h-5 w-5" />
                Actions
              </h3>
              <div className="space-y-4">
                <Button
                  className="w-full"
                  variant="outline"
                  onClick={handleTestParse}
                  disabled={loading}
                  data-testid="test-parse-btn"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  Test Parse (Preview Only)
                </Button>
                <Button
                  className="w-full bg-green-600 hover:bg-green-700"
                  onClick={handleCreateEnquiry}
                  disabled={loading || !selectedCompany}
                  data-testid="create-enquiry-btn"
                >
                  <Send className="h-4 w-4 mr-2" />
                  Create Enquiry from Email
                </Button>
                <p className="text-xs text-muted-foreground text-center">
                  Enquiry will be created under: {companies.find(c => c.id === selectedCompany)?.name || 'Select a company'}
                </p>
              </div>

              {/* Quick Stats */}
              <div className="mt-6 pt-6 border-t">
                <h4 className="font-semibold mb-3">Parser Statistics</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-700">{emailSourcedEnquiries.length}</p>
                    <p className="text-xs text-green-600">Enquiries Created</p>
                  </div>
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <p className="text-2xl font-bold text-blue-700">
                      {emailSourcedEnquiries.filter(e => e.status === 'won').length}
                    </p>
                    <p className="text-xs text-blue-600">Converted to Deals</p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="history" className="space-y-4">
          {emailSourcedEnquiries.length === 0 ? (
            <Card className="p-12 text-center">
              <Mail className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No email-sourced enquiries yet</h3>
              <p className="text-muted-foreground">
                Parse emails to automatically create enquiries
              </p>
            </Card>
          ) : (
            <div className="grid gap-4">
              {emailSourcedEnquiries.map((enquiry) => (
                <Card key={enquiry.id} className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="bg-blue-50 text-blue-700">
                          <Mail className="h-3 w-3 mr-1" />
                          Email Parsed
                        </Badge>
                        <Badge className={
                          enquiry.status === 'won' ? 'bg-green-100 text-green-800' :
                          enquiry.status === 'new' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }>
                          {enquiry.status}
                        </Badge>
                      </div>
                      <h4 className="font-semibold">{enquiry.client_name}</h4>
                      <p className="text-sm text-muted-foreground">{enquiry.client_email}</p>
                      <div className="flex items-center gap-4 mt-2 text-sm">
                        <span className="flex items-center gap-1">
                          <Truck className="h-4 w-4" />
                          {enquiry.asset_type}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {enquiry.duration_months} month(s)
                        </span>
                        <span>Qty: {enquiry.quantity}</span>
                        {enquiry.with_driver && (
                          <Badge variant="outline" className="text-xs">With Driver</Badge>
                        )}
                      </div>
                      {enquiry.original_email?.subject && (
                        <p className="text-xs text-muted-foreground mt-2 italic">
                          Original: "{enquiry.original_email.subject}"
                        </p>
                      )}
                    </div>
                    <div className="text-right text-xs text-muted-foreground">
                      <Clock className="h-3 w-3 inline mr-1" />
                      {new Date(enquiry.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Test Result Dialog */}
      <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              Email Parse Results
            </DialogTitle>
            <DialogDescription>
              Here's what was extracted from the email
            </DialogDescription>
          </DialogHeader>
          
          {testResult && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Client Name</Label>
                  <p className="font-semibold">{testResult.parsed_data?.client_name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Email</Label>
                  <p className="font-semibold">{testResult.parsed_data?.client_email}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Phone</Label>
                  <p className="font-semibold">{testResult.parsed_data?.client_phone || 'Not found'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Asset Type</Label>
                  <p className="font-semibold capitalize">{testResult.parsed_data?.asset_type}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Quantity</Label>
                  <p className="font-semibold">{testResult.parsed_data?.quantity}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Duration</Label>
                  <p className="font-semibold">{testResult.parsed_data?.duration_months} month(s)</p>
                </div>
              </div>
              <div className="flex items-center gap-2 pt-2">
                <Badge variant={testResult.parsed_data?.with_driver ? 'default' : 'outline'}>
                  {testResult.parsed_data?.with_driver ? 'Driver Required' : 'No Driver'}
                </Badge>
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setTestDialogOpen(false)}>
                  Close
                </Button>
                <Button 
                  onClick={() => {
                    setTestDialogOpen(false);
                    handleCreateEnquiry();
                  }}
                  className="bg-green-600 hover:bg-green-700"
                >
                  Create Enquiry
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
